import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import qs from "qs-lite";
import api from "../../utils/api";
import { useHubSnackBar } from "../useSnackBar";
import { clearCachedItem } from "../../helpers";
import { invalidate } from "../../helpers/clientHelper";

export const CUSTOMERS = "/customers";

const getAllCustomers = () => api.get(`${CUSTOMERS}/all`);
export const useGetAllCustomers = (enabled = true) =>
  useQuery([CUSTOMERS, "all"], getAllCustomers, {
    enabled,
  });

const getCustomerDetailsById = ({ queryKey }) => {
  // eslint-disable-next-line
  const [_, id] = queryKey;
  return api.get(`${CUSTOMERS}/${id}`)
}
export const useGetCustomerDetailsById = (id) => {
  return useQuery([CUSTOMERS, id], getCustomerDetailsById, { enabled: !!id });
}
const getCustomerDetails = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${CUSTOMERS}?${queryString}`);
};

export const useGetCustomerDetails = ({ defaultLocation }) => {
  const query = new URLSearchParams(window.location.search);

  const queryLocation = query.get("location");
  const location =
    queryLocation === "All" ? "" : queryLocation || defaultLocation;
  const active = query.get("active") === "false" ? false : true;

  const orderBy = query.get("orderBy") || "name";
  const sortOrder = query.get("sortOrder") || "asc";
  const limit = query.get("limit") || 100;
  const offset = query.get("offset") || 0;

  const queryProps = {
    ...(location ? { location } : {}),
    active,
    limit,
    offset,
    orderBy,
    sortOrder,
  };

  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.cancelQueries({
      predicate: (query) => query.queryKey[0] === CUSTOMERS,
      exact: false,
    });
  }, [location, active, limit, offset, orderBy, sortOrder, queryClient]);

  return useQuery([CUSTOMERS, queryProps], getCustomerDetails, {
    enabled: !!defaultLocation,
    keepPreviousData: true,
  });
};

const postCustomerData = (data) => api.post(CUSTOMERS, data);
export const usePostCustomerData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postCustomerData, {
    onSuccess: () => {
      invalidate(queryClient, CUSTOMERS);
      clearCachedItem("customers");
      addSnackbarSuccess({ message: "Successfully Created Customer" });
    },
  });
};

const putUpdateCustomerActive = (payload) =>
  api.put(`${CUSTOMERS}/active/${payload.id}`, payload.data);
export const usePutUpdateCustomerActive = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putUpdateCustomerActive, {
    onSuccess: () => {
      invalidate(queryClient, CUSTOMERS);
      clearCachedItem("customers");
      addSnackbarSuccess({ message: "Successfully Updated Customer Status" });
    },
  });
};

const putUpdateCustomerData = (payload) => {
  const { id, ...rest } = payload;
  return api.put(`${CUSTOMERS}/${id}`, { ...rest });
};
export const usePutUpdateCustomerData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putUpdateCustomerData, {
    onSuccess: () => {
      invalidate(queryClient, CUSTOMERS);
      clearCachedItem("customers");
      addSnackbarSuccess({ message: "Successfully Updated Customer" });
    },
  });
};

const deleteCustomerById = (id) => api.delete(`${CUSTOMERS}/${id}`);
export const useDeleteCustomerById = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(deleteCustomerById, {
    onSuccess: () => {
      invalidate(queryClient, CUSTOMERS);
      clearCachedItem("customers");
      addSnackbarSuccess({ message: "Successfully Deleted Customer" });
    },
  });
};

const postMergeCustomerData = (data) => {
  const id = data.id;
  delete data.id;
  return api.put(`${CUSTOMERS}/merge/${id}`, data);
};
export const usePostMergeCustomerData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postMergeCustomerData, {
    onSuccess: () => {
      invalidate(queryClient, CUSTOMERS);
      clearCachedItem("customers");
      addSnackbarSuccess({ message: "Successfully Merged Customer" });
    },
  });
};
